import { Component, EventEmitter, inject, OnInit, Output, signal, ViewChild, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { CompanyDetails } from '../company-details/company-details.component';
import { ContactDetails } from '../contact-details/contact-details.component';
import { SurplusLinesLicenseDetailsComponent } from '../surplus-lines-license-details/surplus-lines-license-details.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators, FormArray } from '@angular/forms';
import { noWhitespaceValidator } from '../../../../utils/no-whitespace.validator';
import { AuthService } from '../../../../auth/auth.service';
import { CompanyRequestPayload, StateList } from '../../../../models/SystemAdmin.model';
import { SystemAdminService } from '../../../../services/system-admin.service';
import { ConfirmationDialogComponent } from '../../../utils/confirmation-dialog/confirmation-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ToastService } from '../../../../services/toast.service';
import { CompanyType, CountryType, ToastType } from '../../../../enums/config.enum';
import { UnsavedFormChangesGuard } from '../../../../guards/unsaved-form-changes.guard';
import { CountryISO } from 'ngx-intl-tel-input';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

@Component({
  selector: 'app-add-company',
  standalone: true,
  imports: [CommonModule, MatTabsModule, MatExpansionModule, MatDialogModule, CompanyDetails, 
    ContactDetails, SurplusLinesLicenseDetailsComponent, ReactiveFormsModule],
  templateUrl: './add-company.component.html',
  styleUrl: './add-company.component.scss'
})
export class AddCompanyComponent implements OnInit, OnDestroy, UnsavedFormChangesGuard {
  selectedTab = signal(0);
  companyId = signal<string>('');
  companyTitle = signal<string>('');
  isCreateComapny = signal<boolean>(true);
  router = inject(Router);
  activateRoute = inject(ActivatedRoute);
  authService = inject(AuthService);
  systemAdminService = inject(SystemAdminService);
  toastService = inject(ToastService);
  dialog = inject(MatDialog);
  fb: FormBuilder = inject(FormBuilder);
  countryId: number | null = null;
  @Output() countryChange = new EventEmitter<number>();
  @ViewChild(SurplusLinesLicenseDetailsComponent) surplusLinesComponent!: SurplusLinesLicenseDetailsComponent;
  // Subscriptions for live formatting
  private corporatePhoneSub: Subscription = new Subscription();
  private contactPhoneSub: Subscription = new Subscription();
  private countryIdSub: Subscription = new Subscription();
  private stateIdSub: Subscription = new Subscription();
  private companyTypeSub: Subscription = new Subscription();
  private producerSlaNumbersSub: Subscription = new Subscription();
  companyForm = this.fb.group({
    companyName: [null, [Validators.required, noWhitespaceValidator,Validators.maxLength(128)]],
    corporateMail: [null, [Validators.required, noWhitespaceValidator, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
    companyType: [null, [Validators.required, noWhitespaceValidator]],
    unitNum: [null],
    streetNum: [null,  [Validators.required, noWhitespaceValidator]],
    streetName: [null, [Validators.required, noWhitespaceValidator,Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
    cityId: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(64)]],
    countyId: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(64)]],
    stateId: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(64)]], 
    zipCode: [null,  [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$')]],
    corporatePhone: ['', [this.phoneNumberValidator()]],
    corporatePhoneCountryCode: [CountryISO.UnitedStates, [Validators.required, noWhitespaceValidator]],
    websiteURL: [null, [Validators.required, noWhitespaceValidator, Validators.pattern(/^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=%]*)?$/)]],
    contactPersonName: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
    contactPersonRole: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
    contactPersonEmail: [null, [Validators.required, noWhitespaceValidator, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
    contactPersonPhone: ['', [this.phoneNumberValidator()]],
    contactPersonPhoneCountryCode: [CountryISO.UnitedStates, [Validators.required, noWhitespaceValidator]],
    countryId: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(64)]],
    countryCode: [null as string | null],
    // Surplus lines license details
    slaNumber: [null],
    producerSlaNumbers: this.fb.array([]),
    // Persist SLA config records (configId/slaNumber/etc.) across tab navigation during create flow
    producerSlaConfigList: [<any[]>[]],
    isActive: [1],
    isDeleted: [0]
  }); 
  initialFormValue: any;
  private changesSaved: boolean = false;
  selectedCountry = signal<number | null>(null);
  countryList = signal<any[]>([]);
  CountryISO = CountryISO;
  phoneUtil = PhoneNumberUtil.getInstance();
  CompanyType = CompanyType;
  CountryType = CountryType;

  // Cached US states to determine New Jersey dynamically
  private usStates: StateList[] = [];
  private newJerseyStateId: number | null = null;
  
  // Custom validator for phone number (string format when separateDialCode is true)
  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || (typeof value === 'string' && value.trim().length === 0)) {
        return { required: true };
      }
      
      // Handle string format (when separateDialCode is true)
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed || trimmed.length === 0) {
          return { required: true };
        }
        // Basic validation - check if it has at least some digits
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length < 7) {
          return { validatePhoneNumber: true };
        }
        return null;
      }
      
      // Handle object format (for backward compatibility)
      if (typeof value === 'object' && value.number !== undefined) {
        const phoneNumber = value.number?.trim();
        if (!phoneNumber || phoneNumber.length === 0) {
          return { required: true };
        }
        const digits = phoneNumber.replace(/\D/g, '');
        if (digits.length < 7) {
          return { validatePhoneNumber: true };
        }
        return null;
      }
      
      return { required: true };
    };
  }
  
  // Get all countries from PhoneNumberUtil for dropdown
  getAllCountries(): { code: CountryISO; name: string; displayName: string }[] {
    const countries: { code: CountryISO; name: string; displayName: string }[] = [];
    try {
      // Get all supported region codes
      const supportedRegions = this.phoneUtil.getSupportedRegions();
      for (const region of supportedRegions) {
        try {
          const countryCode = region as CountryISO;
          const countryCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
          const countryName = this.getCountryName(countryCode);
          const displayName = countryCallingCode ? `+${countryCallingCode} ${countryName}` : countryName;
          countries.push({ code: countryCode, name: countryName, displayName });
        } catch {
          // Skip invalid regions
        }
      }
    } catch {
      // Fallback to common countries if getting all regions fails
      countries.push(
        { code: CountryISO.UnitedStates, name: 'United States', displayName: '+1 United States' },
        { code: CountryISO.UnitedKingdom, name: 'United Kingdom', displayName: '+44 United Kingdom' }
      );
    }
    return countries.sort((a, b) => a.name.localeCompare(b.name));
  }
  
  // Get ALL countries with complete details for database insertion
  getAllCountriesForDatabase(): void {
    const allCountries: Array<{
      iso2: string;
      iso3: string | null;
      name: string;
      dialCode: string;
      displayName: string;
    }> = [];
    
    try {
      // Get all supported region codes from google-libphonenumber
      const supportedRegions = this.phoneUtil.getSupportedRegions();
      
      for (const region of supportedRegions) {
        try {
          const iso2 = region.toUpperCase();
          const dialCode = this.phoneUtil.getCountryCodeForRegion(region);
          const countryName = this.getCountryName(region as CountryISO);
          
          // Try to get ISO3 code (if available)
          let iso3: string | null = null;
          try {
            // Some regions might have ISO3 codes, but google-libphonenumber doesn't provide them directly
            // We'll leave it as null for now
            iso3 = null;
          } catch {
            iso3 = null;
          }
          
          const displayName = dialCode ? `+${dialCode} ${countryName}` : countryName;
          
          const countryData = {
            iso2: iso2,
            iso3: iso3,
            name: countryName,
            dialCode: dialCode ? `+${dialCode}` : '',
            displayName: displayName
          };
          
          allCountries.push(countryData);
        } catch (error) {
          // Silently skip errors
        }
      }
      
      // Sort by country name
      allCountries.sort((a, b) => a.name.localeCompare(b.name));
      
      return;
    } catch (error) {
      // Silently handle errors
    }
  }
  
  getCountryName(countryCode: CountryISO): string {
    const codeStr = String(countryCode).toUpperCase();
    
    // Primary: Use browser's Intl.DisplayNames API (standard, supports all countries)
    try {
      const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
      const countryName = displayNames.of(codeStr);
      if (countryName) {
        return countryName;
      }
    } catch (error) {
      // Intl.DisplayNames not supported, fall through to fallback
    }
    
    // Fallback: Only use hardcoded mapping if Intl API is not available
    const countryNames: { [key: string]: string } = {
      'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada', 'AU': 'Australia',
      'FR': 'France', 'DE': 'Germany', 'IT': 'Italy', 'ES': 'Spain', 'NL': 'Netherlands',
      'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria', 'SE': 'Sweden', 'NO': 'Norway',
      'DK': 'Denmark', 'FI': 'Finland', 'PL': 'Poland', 'CZ': 'Czech Republic', 'IE': 'Ireland',
      'PT': 'Portugal', 'GR': 'Greece', 'LU': 'Luxembourg', 'IS': 'Iceland', 'MT': 'Malta',
      'CY': 'Cyprus', 'EE': 'Estonia', 'LV': 'Latvia', 'LT': 'Lithuania', 'SI': 'Slovenia',
      'SK': 'Slovakia', 'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria', 'HR': 'Croatia',
      'JP': 'Japan', 'CN': 'China', 'IN': 'India', 'KR': 'South Korea', 'SG': 'Singapore',
      'MY': 'Malaysia', 'TH': 'Thailand', 'PH': 'Philippines', 'ID': 'Indonesia', 'VN': 'Vietnam',
      'NZ': 'New Zealand', 'ZA': 'South Africa', 'BR': 'Brazil', 'MX': 'Mexico', 'AR': 'Argentina',
      'CL': 'Chile', 'CO': 'Colombia', 'PE': 'Peru', 'AE': 'United Arab Emirates', 'SA': 'Saudi Arabia',
      'IL': 'Israel', 'TR': 'Turkey', 'RU': 'Russia', 'UA': 'Ukraine', 'EG': 'Egypt',
      'NG': 'Nigeria', 'KE': 'Kenya', 'GH': 'Ghana', 'MA': 'Morocco', 'TN': 'Tunisia'
    };
    
    if (countryNames[codeStr]) {
      return countryNames[codeStr];
    }
    
    // Final fallback: format the code nicely
    return codeStr.split('').map((char, i) => i === 0 ? char.toUpperCase() : char.toLowerCase()).join('');
  }

  ngOnInit(): void {
    this.getCountryList();
    
    // Expose method to window for easy access from browser console
    // You can call: (window as any).getAllCountriesForDatabase() from browser console
    (window as any).getAllCountriesForDatabase = () => this.getAllCountriesForDatabase();
    
    // To automatically log all countries on component load, uncomment the line below:
    // this.getAllCountriesForDatabase();
    
    this.activateRoute.params.subscribe({
      next: (params) => {
        if(params && params['companyId']){
          this.companyId.set(params['companyId']);
          this.fetchCompanyDetails(params['companyId']); // getCompany Details
          this.isCreateComapny.set(false);
          this.companyForm.get('isActive')?.enable();
        }else{
          // addNewCompany form view
          this.companyForm.get('isActive')?.disable();
        }
      }
    });
    this.initialFormValue = this.companyForm.getRawValue(); // Store the initial form value
    // Live input formatting subscriptions for corporate and contact phones
    const corporateCtrl = this.companyForm.get('corporatePhone');
    const contactCtrl = this.companyForm.get('contactPersonPhone');
    const countryIdCtrl = this.companyForm.get('countryId');
    const stateIdCtrl = this.companyForm.get('stateId');
    const companyTypeCtrl = this.companyForm.get('companyType');
    const producerSlaNumbersArr = this.companyForm.get('producerSlaNumbers') as FormArray | null;
    
    if (corporateCtrl) {
      this.corporatePhoneSub = corporateCtrl.valueChanges.subscribe((value: any) => {
        if (value !== null && value !== undefined) {
          // When separateDialCode is true, ngx-intl-tel-input returns a string (phone number only)
          if (typeof value === 'string') {
            // Format the phone number based on country code
            const countryCode = this.companyForm.get('corporatePhoneCountryCode')?.value || CountryISO.UnitedStates;
            // Extract digits directly from input (don't use extractRawPhoneNumber for real-time formatting)
            const digits = value.replace(/\D/g, '');
            const maxLength = this.getMaxPhoneLength(countryCode);
            
            // If digits exceed max length, truncate IMMEDIATELY to prevent excessive input
            if (digits.length > maxLength) {
              const truncated = digits.substring(0, maxLength);
              const truncatedFormatted = this.formatPhoneNumber(truncated, countryCode);
              corporateCtrl.setValue(truncatedFormatted || truncated, { emitEvent: false });
              corporateCtrl.updateValueAndValidity({ emitEvent: false });
              return;
            }
            
            // Format the raw digits directly (consistent extraction for real-time formatting)
            // Don't use extractRawPhoneNumber here as it's meant for already-formatted numbers
            const formatted = this.formatPhoneNumber(digits, countryCode);
            
            // For UK, ALWAYS apply formatting to ensure consistency
            // Use a more aggressive approach to override component's internal formatting
            if (formatted) {
              if (countryCode === CountryISO.UnitedKingdom) {
                // Store the expected formatted value
                const expectedFormatted = formatted;
                
                // Apply formatting immediately
                corporateCtrl.setValue(expectedFormatted, { emitEvent: false });
                
                // Use multiple timing strategies to ensure formatting sticks
                // Strategy 1: requestAnimationFrame (next frame)
                requestAnimationFrame(() => {
                  const currentValue = corporateCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== expectedFormatted) {
                      corporateCtrl.setValue(expectedFormatted, { emitEvent: false });
                    }
                  }
                });
                
                // Strategy 2: setTimeout with short delay
                setTimeout(() => {
                  const currentValue = corporateCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== expectedFormatted) {
                      corporateCtrl.setValue(expectedFormatted, { emitEvent: false });
                    }
                  }
                }, 50);
                
                // Strategy 3: setTimeout with longer delay as final fallback
                setTimeout(() => {
                  const currentValue = corporateCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== expectedFormatted) {
                      corporateCtrl.setValue(expectedFormatted, { emitEvent: false });
                    }
                  }
                }, 200);
              } else if (formatted !== value) {
                corporateCtrl.setValue(formatted, { emitEvent: false });
              }
            }
          } else if (value && typeof value === 'object' && value.number !== undefined) {
            // Handle object format (for backward compatibility)
            const countryCode = value.countryCode || this.companyForm.get('corporatePhoneCountryCode')?.value || CountryISO.UnitedStates;
            if (value.countryCode) {
              this.companyForm.get('corporatePhoneCountryCode')?.setValue(value.countryCode, { emitEvent: false });
            }
            // Convert to string format for separateDialCode mode
            const rawNumber = this.extractRawPhoneNumber(value.number, countryCode);
            const formatted = this.formatPhoneNumber(rawNumber, countryCode);
            corporateCtrl.setValue(formatted || '', { emitEvent: false });
          }
        }
      });
    }
    if (contactCtrl) {
      this.contactPhoneSub = contactCtrl.valueChanges.subscribe((value: any) => {
        if (value !== null && value !== undefined) {
          // When separateDialCode is true, ngx-intl-tel-input returns a string (phone number only)
          if (typeof value === 'string') {
            // Format the phone number based on country code
            const countryCode = this.companyForm.get('contactPersonPhoneCountryCode')?.value || CountryISO.UnitedStates;
            // Extract digits directly from input (don't use extractRawPhoneNumber for real-time formatting)
            const digits = value.replace(/\D/g, '');
            const maxLength = this.getMaxPhoneLength(countryCode);
            
            // If digits exceed max length, truncate IMMEDIATELY to prevent excessive input
            if (digits.length > maxLength) {
              const truncated = digits.substring(0, maxLength);
              const truncatedFormatted = this.formatPhoneNumber(truncated, countryCode);
              contactCtrl.setValue(truncatedFormatted || truncated, { emitEvent: false });
              contactCtrl.updateValueAndValidity({ emitEvent: false });
              return;
            }
            
            // Format the raw digits directly (consistent extraction for real-time formatting)
            // Don't use extractRawPhoneNumber here as it's meant for already-formatted numbers
            const formatted = this.formatPhoneNumber(digits, countryCode);
            
            // For UK, ALWAYS apply formatting to ensure consistency
            // Use a more aggressive approach to override component's internal formatting
            if (formatted) {
              if (countryCode === CountryISO.UnitedKingdom) {
                // Store the expected formatted value
                const expectedFormatted = formatted;
                
                // Apply formatting immediately
                contactCtrl.setValue(expectedFormatted, { emitEvent: false });
                
                // Use multiple timing strategies to ensure formatting sticks
                // Strategy 1: requestAnimationFrame (next frame)
                requestAnimationFrame(() => {
                  const currentValue = contactCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== expectedFormatted) {
                      contactCtrl.setValue(expectedFormatted, { emitEvent: false });
                    }
                  }
                });
                
                // Strategy 2: setTimeout with short delay
                setTimeout(() => {
                  const currentValue = contactCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== expectedFormatted) {
                      contactCtrl.setValue(expectedFormatted, { emitEvent: false });
                    }
                  }
                }, 50);
                
                // Strategy 3: setTimeout with longer delay as final fallback
                setTimeout(() => {
                  const currentValue = contactCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== expectedFormatted) {
                      contactCtrl.setValue(expectedFormatted, { emitEvent: false });
                    }
                  }
                }, 200);
              } else if (formatted !== value) {
                contactCtrl.setValue(formatted, { emitEvent: false });
              }
            }
          } else if (value && typeof value === 'object' && value.number !== undefined) {
            // Handle object format (for backward compatibility)
            const countryCode = value.countryCode || this.companyForm.get('contactPersonPhoneCountryCode')?.value || CountryISO.UnitedStates;
            if (value.countryCode) {
              this.companyForm.get('contactPersonPhoneCountryCode')?.setValue(value.countryCode, { emitEvent: false });
            }
            // Convert to string format for separateDialCode mode
            const rawNumber = this.extractRawPhoneNumber(value.number, countryCode);
            const formatted = this.formatPhoneNumber(rawNumber, countryCode);
            contactCtrl.setValue(formatted || '', { emitEvent: false });
          }
        }
      });
    }
    
    // Subscribe to country code changes to reformat phone numbers
    const corporatePhoneCountryCodeCtrl = this.companyForm.get('corporatePhoneCountryCode');
    const contactPersonPhoneCountryCodeCtrl = this.companyForm.get('contactPersonPhoneCountryCode');
    
    if (corporatePhoneCountryCodeCtrl) {
      corporatePhoneCountryCodeCtrl.valueChanges.subscribe((countryCode: CountryISO | null) => {
        if (countryCode) {
          // Reset phone number field when country code changes
          corporateCtrl?.setValue('', { emitEvent: false });
        }
      });
    }
    
    if (contactPersonPhoneCountryCodeCtrl) {
      contactPersonPhoneCountryCodeCtrl.valueChanges.subscribe((countryCode: CountryISO | null) => {
        if (countryCode) {
          // Reset phone number field when country code changes
          contactCtrl?.setValue('', { emitEvent: false });
        }
      });
    }
    if (countryIdCtrl) {
      this.countryIdSub = countryIdCtrl.valueChanges.subscribe((countryId: any) => {
        if (countryId !== null && countryId !== undefined) {
          // Update countryCode when countryId changes
          const countryIdNum = typeof countryId === 'string' ? Number(countryId) : countryId;
          this.countryId = countryIdNum;
          
          // Ensure countryList is loaded
          if (this.countryList().length > 0) {
            const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryIdNum));
            const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
            this.companyForm.get('countryCode')?.setValue(countryCode, { emitEvent: false });
          }

          if (Number(countryIdNum) === CountryType.US) {
            this.loadUSStatesIfNeeded();
          }

          this.updateSlaNumberValidator();
        }
      });
    }

    if (stateIdCtrl) {
      this.stateIdSub = stateIdCtrl.valueChanges.subscribe(() => {
        this.updateSlaNumberValidator();
      });
    }

    if (companyTypeCtrl) {
      this.companyTypeSub = companyTypeCtrl.valueChanges.subscribe(() => {
        this.updateSlaNumberValidator();
      });
    }

    // Re-evaluate SLA field required-ness whenever the grid changes
    if (producerSlaNumbersArr) {
      this.producerSlaNumbersSub = producerSlaNumbersArr.valueChanges.subscribe(() => {
        this.updateSlaNumberValidator();
      });
    }

    this.updateSlaNumberValidator();
  }

  getCountryList() {
    this.systemAdminService.getCountryList().subscribe({
      next: (response: any) => {
        if(response){
          this.countryList.set(response);
          // If countryId is already set, update countryCode
          const currentCountryId = this.companyForm.get('countryId')?.value;
          if (currentCountryId) {
            this.updateCountryCode(typeof currentCountryId === 'string' ? Number(currentCountryId) : currentCountryId);
          }
        }
      },
      error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    })
  }

  fetchCompanyDetails(companyId: string) {
    this.systemAdminService.getCompanyDetails(companyId).subscribe({
      next:(response: any) => {
        if(response){
          this.companyTitle.set(response?.companyName);
          this.onBindFormData(response);
        }
      },
      error: (error: any) => {
        console.log("error", error);
        // show error notification
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    })
  }

 

  onCountryChange(selectedCountryId: number | string) {
    // Convert to number if it's a string
    const countryIdNum = typeof selectedCountryId === 'string' ? Number(selectedCountryId) : selectedCountryId;
    this.countryId = countryIdNum; // Store the selected country ID
    
    // Ensure countryList is loaded before trying to find the country
    if (this.countryList().length === 0) {
      // If countryList is not loaded yet, wait for it
      setTimeout(() => {
        this.updateCountryCode(countryIdNum);
      }, 100);
      return;
    }
    
    this.updateCountryCode(countryIdNum);
    this.formatPhoneNumberInputs(); // Call the method to format phone numbers
  }

  private updateCountryCode(countryId: number) {
    // Set countryCode in form based on selected country
    // Compare both as numbers to handle type mismatches
    const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
    const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
    
    this.companyForm.get('countryCode')?.setValue(countryCode);
  }

formatPhoneNumberInputs() {

  let corporatePhoneValue: any = this.companyForm.get('corporatePhone')?.value ?? null;
  let contactPersonPhoneValue: any = this.companyForm.get('contactPersonPhone')?.value ?? null;
  const corporatePhoneCountryCode = this.companyForm.get('corporatePhoneCountryCode')?.value;
  const contactPersonPhoneCountryCode = this.companyForm.get('contactPersonPhoneCountryCode')?.value;

  // Extract phone number from string or object format
  const corporatePhoneNumber = corporatePhoneValue && typeof corporatePhoneValue === 'object' && corporatePhoneValue.number !== undefined
    ? corporatePhoneValue.number 
    : (corporatePhoneValue !== null ? String(corporatePhoneValue) : null);
  const contactPersonPhoneNumber = contactPersonPhoneValue && typeof contactPersonPhoneValue === 'object' && contactPersonPhoneValue.number !== undefined
    ? contactPersonPhoneValue.number
    : (contactPersonPhoneValue !== null ? String(contactPersonPhoneValue) : null);

  // Extract raw numbers
  const corporateRaw = this.extractRawPhoneNumber(corporatePhoneNumber, corporatePhoneCountryCode || undefined);
  const contactRaw = this.extractRawPhoneNumber(contactPersonPhoneNumber, contactPersonPhoneCountryCode || undefined);

  // Format with selected country code - use string format when separateDialCode is true
  const corporateCountryCode = corporatePhoneCountryCode || CountryISO.UnitedStates;
  const contactCountryCode = contactPersonPhoneCountryCode || CountryISO.UnitedStates;
  
  this.companyForm.get('corporatePhone')?.setValue(this.formatPhoneNumber(corporateRaw, corporateCountryCode) || '');
  this.companyForm.get('contactPersonPhone')?.setValue(this.formatPhoneNumber(contactRaw, contactCountryCode) || '');
}


formatPhoneNumber(phoneNumber: string | number | null | undefined, countryCode?: CountryISO | null): string | null {
  
  // Convert number to string
  if (typeof phoneNumber === "number") {
    phoneNumber = phoneNumber.toString();
  }

  // If phoneNumber is still invalid, return null
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return null;
  }

  // Use provided countryCode or default to address country
  const selectedCountryCode = countryCode || this.getCountryCodeFromCountryId(this.countryId);
  
  if (!selectedCountryCode) {
    return phoneNumber;
  }

  try {
    // Remove all non-digit characters for parsing
    const cleanedNumber = phoneNumber.replace(/\D/g, '');
    
    // Special handling for UK to ensure consistent formatting - ALWAYS use this for UK
    if (selectedCountryCode === CountryISO.UnitedKingdom) {
      // UK numbers: 10 digits (mobile) or 11 digits (landline)
      if (cleanedNumber.length <= 4) {
        return cleanedNumber;
      } else if (cleanedNumber.length <= 7) {
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
      } else if (cleanedNumber.length === 10) {
        // 10 digits: XXXX XXXXXX (e.g., 7655 677666)
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
      } else if (cleanedNumber.length === 11) {
        // 11 digits: XXXX XXX XXXX (e.g., 7655 677 6666)
        return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
      } else {
        // More than 11 digits: truncate to 11 and format
        const truncated = cleanedNumber.substring(0, 11);
        return `${truncated.slice(0, 4)} ${truncated.slice(4, 7)} ${truncated.slice(7, 11)}`;
      }
    }
    
    // Parse the phone number with the selected country code
    const parsedNumber = this.phoneUtil.parse(cleanedNumber, selectedCountryCode);
    const nationalNumber = parsedNumber.getNationalNumber();
    const countryCode = parsedNumber.getCountryCode();
    
    // Format as national number (without country code) since country code is displayed separately
    try {
      const formatted = this.phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL);
      
      // Remove any leading country code that might be included
      let finalFormatted = formatted.replace(/^\+\d+\s*/, '').trim();
      // Remove leading "0" that NATIONAL format adds for some countries (e.g., Afghanistan, UK)
      // Since country code is displayed separately, we don't need this leading "0"
      finalFormatted = finalFormatted.replace(/^0+/, '').trim();
      return finalFormatted;
    } catch (formatError) {
      const progressiveResult = this.formatPhoneNumberProgressive(cleanedNumber, selectedCountryCode);
      return progressiveResult;
    }
  } catch (error) {
    const fallbackResult = this.formatPhoneNumberFallback(phoneNumber, selectedCountryCode);
    return fallbackResult;
  }
}

private formatPhoneNumberProgressive(cleanedNumber: string, countryCode: CountryISO | null | undefined): string {
  if (!countryCode || !cleanedNumber) {
    return cleanedNumber;
  }
  
  // Format progressively based on country
  if (countryCode === CountryISO.UnitedStates) {
    // US format: (XXX) XXX-XXXX
    if (cleanedNumber.length <= 3) {
      return cleanedNumber.length > 0 ? `(${cleanedNumber}` : '';
    } else if (cleanedNumber.length <= 6) {
      return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3)}`;
    } else {
      return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3, 6)}-${cleanedNumber.slice(6, 10)}`;
    }
  } else if (countryCode === CountryISO.UnitedKingdom) {
    // UK format: XXXX XXX XXXX or variations
    if (cleanedNumber.length <= 4) {
      return cleanedNumber;
    } else if (cleanedNumber.length <= 7) {
      return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
    } else {
      return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
    }
  }
  
  // Default: return as is
  return cleanedNumber;
}

private formatPhoneNumberFallback(phoneNumber: string, countryCode: CountryISO | null | undefined): string {
  if (!countryCode) {
    return phoneNumber;
  }
  
  const cleanedNumber = phoneNumber.replace(/\D/g, '');
  
  // Get country code prefix
  const countryCodePrefix = this.getCountryCodePrefix(countryCode);

  // Remove country code prefix if already present
  let digits = cleanedNumber;
  if (countryCodePrefix) {
    const prefixDigits = countryCodePrefix.replace(/\D/g, '');
    if (digits.startsWith(prefixDigits)) {
      digits = digits.substring(prefixDigits.length);
    }
  }

  // Format based on country (without country code prefix since it's displayed separately)
  if (countryCode === CountryISO.UnitedStates && digits.length === 10) {
    const match = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  } else if (countryCode === CountryISO.UnitedStates && digits.length > 0) {
    // Progressive formatting for US
    return this.formatPhoneNumberProgressive(digits, countryCode);
  } else if (countryCode === CountryISO.UnitedKingdom && digits.length >= 10) {
    // UK numbers can vary, try common format
    if (digits.length === 11) {
      const match = digits.match(/^(\d{4})(\d{3})(\d{4})$/);
      if (match) {
        return `${match[1]} ${match[2]} ${match[3]}`;
      }
    }
    // Progressive formatting for UK
    return this.formatPhoneNumberProgressive(digits, countryCode);
  }

  return digits;
}

private getCountryCodeFromCountryId(countryId: number | null): CountryISO | null {
  if (!countryId) return null;
  // Map countryId to CountryISO
  // Assuming countryId 1 = US, countryId 2 = UK
  if (countryId === 1) return CountryISO.UnitedStates;
  if (countryId === 2) return CountryISO.UnitedKingdom;
  return null;
}

  private getCountryCodePrefix(countryCode: CountryISO | null | undefined): string {
    if (!countryCode) {
      return '';
    }
    try {
      const callingCode = this.phoneUtil.getCountryCodeForRegion(countryCode);
      if (callingCode) {
        const regionCode = this.phoneUtil.getRegionCodeForCountryCode(callingCode);
        return `+${callingCode}`;
      }
      return '';
    } catch {
      return '';
    }
  }

// Get country calling code from CountryISO (e.g., CountryISO.Afghanistan -> "+93")
private getCountryCallingCode(countryCode: CountryISO | null | undefined): string | null {
  if (!countryCode) {
    return null;
  }
  try {
    const callingCode = this.phoneUtil.getCountryCodeForRegion(countryCode);
    return callingCode ? `+${callingCode}` : null;
  } catch {
    return null;
  }
}

// Convert calling code (e.g., "+93") back to CountryISO (e.g., CountryISO.Afghanistan)
private mapCallingCodeToISO(callingCode: string | number | null | undefined): CountryISO | null {
  if (!callingCode) {
    return null;
  }
  
  // Remove + sign if present and convert to number
  const codeStr = String(callingCode).replace(/^\+/, '').trim();
  const codeNum = parseInt(codeStr, 10);
  
  if (isNaN(codeNum)) {
    return null;
  }
  
  try {
    // Iterate through all supported regions to find the one with matching calling code
    const supportedRegions = this.phoneUtil.getSupportedRegions();
    for (const region of supportedRegions) {
      try {
        const regionCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
        if (regionCallingCode === codeNum) {
          // Found matching region, return as CountryISO (uppercase)
          return region.toUpperCase() as CountryISO;
        }
      } catch {
        // Skip invalid regions
        continue;
      }
    }
  } catch (error) {
  }
  
  // Fallback: try to match common codes manually
  try {
    if (codeNum === 1) return CountryISO.UnitedStates;
    if (codeNum === 44) return CountryISO.UnitedKingdom;
    if (codeNum === 93) return CountryISO.Afghanistan;
    if (codeNum === 33) return CountryISO.France;
    if (codeNum === 49) return CountryISO.Germany;
    if (codeNum === 39) return CountryISO.Italy;
    if (codeNum === 34) return CountryISO.Spain;
    if (codeNum === 31) return CountryISO.Netherlands;
    if (codeNum === 32) return CountryISO.Belgium;
    if (codeNum === 41) return CountryISO.Switzerland;
    if (codeNum === 43) return CountryISO.Austria;
    if (codeNum === 46) return CountryISO.Sweden;
    if (codeNum === 47) return CountryISO.Norway;
    if (codeNum === 45) return CountryISO.Denmark;
    if (codeNum === 358) return CountryISO.Finland;
    if (codeNum === 48) return CountryISO.Poland;
    if (codeNum === 420) return CountryISO.CzechRepublic;
    if (codeNum === 353) return CountryISO.Ireland;
    if (codeNum === 351) return CountryISO.Portugal;
    if (codeNum === 30) return CountryISO.Greece;
    if (codeNum === 352) return CountryISO.Luxembourg;
    if (codeNum === 354) return CountryISO.Iceland;
    if (codeNum === 356) return CountryISO.Malta;
    if (codeNum === 357) return CountryISO.Cyprus;
    if (codeNum === 372) return CountryISO.Estonia;
    if (codeNum === 371) return CountryISO.Latvia;
    if (codeNum === 370) return CountryISO.Lithuania;
    if (codeNum === 386) return CountryISO.Slovenia;
    if (codeNum === 421) return CountryISO.Slovakia;
    if (codeNum === 36) return CountryISO.Hungary;
    if (codeNum === 40) return CountryISO.Romania;
    if (codeNum === 359) return CountryISO.Bulgaria;
    if (codeNum === 385) return CountryISO.Croatia;
  } catch {}
  
  return null;
}






  formatUSTelephone(value: string): string {
    
    // Format US phone number
    if (!value) return '';
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = RegExp(/^(\d{3})(\d{3})(\d{4})$/).exec(cleaned);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : value;
  }

  formatUKTelephone(value: string): string {
    
    // Format UK phone number
    if (!value) return '';
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = RegExp(/^(\d{2})(\d{4})(\d{4})$/).exec(cleaned);
    return match ? `${match[1]} ${match[2]} ${match[3]}` : value;
  }

  onBindFormData(formValue: any) {

    this.companyForm.patchValue({
      companyType: formValue?.companyType,
      corporateMail: formValue?.corporateMail,
      companyName: formValue?.companyName,
      unitNum: formValue?.address?.unitNum,
      streetNum: formValue?.address?.streetNum,
      streetName: formValue?.address?.streetName,
      cityId: formValue?.address?.cityId,
      countyId: formValue?.address?.countyId,
      stateId: formValue?.address?.stateId,
      zipCode: formValue?.address?.zipCode,
      websiteURL: formValue?.websiteURL,
      contactPersonName: formValue?.contactPersonName,
      contactPersonRole: formValue?.contactPersonRole,
      contactPersonEmail: formValue?.contactPersonEmail,
      // countryId: formValue?.address?.countryId,
      isActive: formValue?.isActive,
      isDeleted: formValue?.isDeleted
    });
    
    // Get phone numbers and convert to string if they're numbers
    // Check both corporatePhone and iPhone fields (iPhone might contain formatted number with country code)
    const corporatePhone = formValue?.corporatePhone ?? formValue?.iPhone?.replace(/^\+\d+/, '') ?? null;
    const contactPersonPhone = formValue?.contactPersonPhone ?? formValue?.contactPersonIPhone?.replace(/^\+\d+/, '') ?? null;
    const countryId = formValue?.address?.countryId ?? null;
    // The countryCode field now contains calling code (e.g., "+93") instead of ISO code
    const countryCodeFromResponse = formValue?.countryCode ?? null;
    // The contactCountryCode field contains calling code for contact person phone (e.g., "+1")
    const contactCountryCodeFromResponse = formValue?.contactCountryCode ?? null;
    
    // Get ISO2 codes from API response (prioritize these over calling codes)
    // Check both uppercase and lowercase property names for compatibility
    const corporatePhoneCodeFromResponse = formValue?.CorporatePhoneCode ?? formValue?.corporatePhoneCode ?? null;
    const contactPhoneCodeFromResponse = formValue?.ContactPhoneCode ?? formValue?.contactPhoneCode ?? null;
    
    // Check if phone codes are missing (for existing records)
    const phoneCodesMissing = !corporatePhoneCodeFromResponse && !contactPhoneCodeFromResponse && 
                              !countryCodeFromResponse && !contactCountryCodeFromResponse;
    
    // Convert ISO2 codes to CountryISO enum (if provided)
    // IMPORTANT: Use ISO2 codes directly from API response to avoid ambiguity
    // (e.g., +1 can be US, Canada, Anguilla, Antigua, etc. - but ISO2 "AI" is uniquely Anguilla)
    let corporatePhoneCodeISO: CountryISO | null = null;
    let contactPhoneCodeISO: CountryISO | null = null;
    
    if (corporatePhoneCodeFromResponse) {
      const iso2Upper = String(corporatePhoneCodeFromResponse).toUpperCase().trim();
      // Directly use the ISO2 code as CountryISO (CountryISO enum values are ISO2 codes)
      // This ensures we use the exact ISO2 code from API response (e.g., "AI" for Anguilla)
      if (iso2Upper && iso2Upper.length === 2) {
        corporatePhoneCodeISO = iso2Upper as CountryISO;
      }
    }
    
    if (contactPhoneCodeFromResponse) {
      const iso2Upper = String(contactPhoneCodeFromResponse).toUpperCase().trim();
      // Directly use the ISO2 code as CountryISO
      if (iso2Upper && iso2Upper.length === 2) {
        contactPhoneCodeISO = iso2Upper as CountryISO;
      }
    }
    
    // If phone codes are missing and we have countryId, use it to determine phone country codes
    if (phoneCodesMissing && countryId) {
      const countryCodeFromCountryId = this.getCountryCodeFromCountryId(countryId);
      if (countryCodeFromCountryId) {
        // Use countryId to set both phone codes
        corporatePhoneCodeISO = countryCodeFromCountryId;
        contactPhoneCodeISO = countryCodeFromCountryId;
      }
    }
    
    // Use ISO2 codes directly if available, otherwise fall back to calling code conversion
    // CRITICAL: Always prioritize ISO2 codes from API response to avoid ambiguity
    // When corporatePhoneCodeISO is set (e.g., "AI"), use it directly - don't fall back to calling code conversion
    const corporatePhoneCountryCode = corporatePhoneCodeISO ?? 
      (countryCodeFromResponse ? this.mapCallingCodeToISO(countryCodeFromResponse) : null) ??
      this.getCountryCodeFromCountryId(countryId) ?? 
      CountryISO.UnitedStates;
    
    const contactPersonPhoneCountryCode = contactPhoneCodeISO ?? 
      (contactCountryCodeFromResponse ? this.mapCallingCodeToISO(String(contactCountryCodeFromResponse)) : null) ??
      corporatePhoneCodeISO ??
      this.getCountryCodeFromCountryId(countryId) ?? 
      CountryISO.UnitedStates;
    
    
    this.countryId = countryId;
    this.companyForm.get('countryId')?.setValue(countryId);
    
    // Set phone country codes FIRST (before formatting phone numbers)
    // CRITICAL: If ISO2 code is available from API response, use it directly
    // This avoids ambiguity when multiple countries share the same calling code (e.g., +1)
    if (corporatePhoneCodeISO) {
      // Use ISO2 code directly from API response (e.g., "AI" for Anguilla)
      this.companyForm.get('corporatePhoneCountryCode')?.setValue(corporatePhoneCodeISO, { emitEvent: false });
    } else {
      // Fallback to calling code conversion only if ISO2 is not available
      this.companyForm.get('corporatePhoneCountryCode')?.setValue(corporatePhoneCountryCode, { emitEvent: false });
    }
    
    if (contactPhoneCodeISO) {
      // Use ISO2 code directly from API response
      this.companyForm.get('contactPersonPhoneCountryCode')?.setValue(contactPhoneCodeISO, { emitEvent: false });
    } else {
      // Fallback to calling code conversion only if ISO2 is not available
      this.companyForm.get('contactPersonPhoneCountryCode')?.setValue(contactPersonPhoneCountryCode, { emitEvent: false });
    }
    
    // Ensure the ISO2 codes are set after a brief delay to allow ngx-intl-tel-input to initialize
    // This is important because the component might not recognize the country code immediately
    setTimeout(() => {
      if (corporatePhoneCodeISO) {
        this.companyForm.get('corporatePhoneCountryCode')?.setValue(corporatePhoneCodeISO, { emitEvent: false });
      }
      if (contactPhoneCodeISO) {
        this.companyForm.get('contactPersonPhoneCountryCode')?.setValue(contactPhoneCodeISO, { emitEvent: false });
      }
    }, 100);
    
    // Set countryCode in form - use the calling code from response
    // The countryCode field stores the calling code (e.g., "+93") from phone dropdown
    if (countryCodeFromResponse) {
      // Use countryCode from response (calling code like "+93")
      this.companyForm.get('countryCode')?.setValue(String(countryCodeFromResponse), { emitEvent: false });
    } else if (corporatePhoneCountryCode) {
      // If no countryCode in response but we have phone country code, convert to calling code
      const callingCode = this.getCountryCallingCode(corporatePhoneCountryCode);
      if (callingCode) {
        this.companyForm.get('countryCode')?.setValue(callingCode, { emitEvent: false });
      }
    } else if (countryId !== null && this.countryList().length > 0) {
      // Fallback: look up from countryList based on address countryId
      const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
      const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
      this.companyForm.get('countryCode')?.setValue(countryCode, { emitEvent: false });
    } else if (countryId !== null) {
      // If countryList is not loaded yet, wait for it
      setTimeout(() => {
        const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
        const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
        this.companyForm.get('countryCode')?.setValue(countryCode, { emitEvent: false });
        // Reformat phone numbers after countryCode is set
        this.formatPhoneNumbersForBinding(corporatePhone, contactPersonPhone, corporatePhoneCountryCode, contactPersonPhoneCountryCode);
      }, 200);
      return; // Exit early, will format after timeout
    }
    
    // Format phone numbers AFTER country codes are set
    // Use setTimeout to ensure form controls are updated
    setTimeout(() => {
      this.formatPhoneNumbersForBinding(corporatePhone, contactPersonPhone, corporatePhoneCountryCode, contactPersonPhoneCountryCode);
    }, 0);

    if(String(formValue?.companyType) === String(CompanyType.BROKER_COMPANY)){
      // this.companyForm.get('countryId')?.disable();
    }
  }

  private formatPhoneNumbersForBinding(corporatePhone: any, contactPersonPhone: any, corporatePhoneCountryCode?: CountryISO, contactPersonPhoneCountryCode?: CountryISO) {
    // Convert phone numbers to string if they're numbers or objects
    let corporatePhoneStr: string | null = null;
    let contactPersonPhoneStr: string | null = null;
    
    if (corporatePhone !== null && corporatePhone !== undefined) {
      if (typeof corporatePhone === 'object' && corporatePhone.number !== undefined) {
        corporatePhoneStr = String(corporatePhone.number);
      } else {
        corporatePhoneStr = String(corporatePhone);
      }
    }
    
    if (contactPersonPhone !== null && contactPersonPhone !== undefined) {
      if (typeof contactPersonPhone === 'object' && contactPersonPhone.number !== undefined) {
        contactPersonPhoneStr = String(contactPersonPhone.number);
      } else {
        contactPersonPhoneStr = String(contactPersonPhone);
      }
    }
    
    // Use provided country codes or get from form (should already be set)
    const corporateCountryCode = corporatePhoneCountryCode || this.companyForm.get('corporatePhoneCountryCode')?.value || CountryISO.UnitedStates;
    const contactCountryCode = contactPersonPhoneCountryCode || this.companyForm.get('contactPersonPhoneCountryCode')?.value || CountryISO.UnitedStates;
    
    // Format phone numbers (string format when separateDialCode is true)
    // Extract raw digits first, then format
    let corporateRaw = corporatePhoneStr ? this.extractRawPhoneNumber(corporatePhoneStr, corporateCountryCode) : null;
    let contactRaw = contactPersonPhoneStr ? this.extractRawPhoneNumber(contactPersonPhoneStr, contactCountryCode) : null;
    
    // Special handling for UK: if 11 digits, cut the last digit (UK numbers should be 10 digits for mobile or 11 with leading 0)
    if (corporateCountryCode === CountryISO.UnitedKingdom && corporateRaw && corporateRaw.length === 11 && !corporateRaw.startsWith('0')) {
      corporateRaw = corporateRaw.substring(0, 10);
    }
    if (contactCountryCode === CountryISO.UnitedKingdom && contactRaw && contactRaw.length === 11 && !contactRaw.startsWith('0')) {
      contactRaw = contactRaw.substring(0, 10);
    }
    
    const formattedCorporatePhone = corporateRaw ? this.formatPhoneNumber(corporateRaw, corporateCountryCode) : null;
    const formattedContactPhone = contactRaw ? this.formatPhoneNumber(contactRaw, contactCountryCode) : null;
    
    // Set as string values (when separateDialCode is true)
    this.companyForm.get('corporatePhone')?.setValue(formattedCorporatePhone || '');
    this.companyForm.get('contactPersonPhone')?.setValue(formattedContactPhone || '');
  }
  
  private mapCountryCodeToISO(countryCode: string | number): CountryISO {
    // Map country code string/number to CountryISO enum
    const codeStr = String(countryCode).toUpperCase();
    if (codeStr === 'US' || codeStr === '1' || codeStr === '+1') return CountryISO.UnitedStates;
    if (codeStr === 'GB' || codeStr === 'UK' || codeStr === '44' || codeStr === '+44') return CountryISO.UnitedKingdom;
    // Try to find matching country by code
    try {
      const codeNum = parseInt(codeStr.replace(/\D/g, ''));
      if (codeNum === 1) return CountryISO.UnitedStates;
      if (codeNum === 44) return CountryISO.UnitedKingdom;
    } catch {}
    return CountryISO.UnitedStates; // Default
  }


  onTabChange(index: number): void {
    this.selectedTab.set(index);
  }

  onNext(): void {
    const current = this.selectedTab();
    const hasSurplus = this.hasSurplusTab();
    const maxIndex = hasSurplus ? 2 : 1; // 0 - Company, 1 - Contact, 2 - Surplus (if broker)
    const nextIndex = current + 1;

    if (nextIndex <= maxIndex) {
      this.selectedTab.set(nextIndex);
    }
  }

  onPrevious(): void {
    const current = this.selectedTab();
    const prevIndex = current - 1;
    if (prevIndex >= 0) {
      this.selectedTab.set(prevIndex);
    }
  }

  isCompanyDetailsFieldsValid(): boolean{
    return (
      !this.companyForm.get('companyName')?.invalid &&
      !this.companyForm.get('corporateMail')?.invalid &&
      !this.companyForm.get('companyType')?.invalid &&
      !this.companyForm.get('unitNum')?.invalid &&
      !this.companyForm.get('streetNum')?.invalid &&
      !this.companyForm.get('streetName')?.invalid &&
      !this.companyForm.get('cityId')?.invalid &&
      !this.companyForm.get('countyId')?.invalid &&
      !this.companyForm.get('stateId')?.invalid &&
      !this.companyForm.get('zipCode')?.invalid &&
      !this.companyForm.get('corporatePhone')?.invalid &&
      !this.companyForm.get('websiteURL')?.invalid
    );
  }

  isContactDetailsFieldsVaild(): boolean {
    return (
      !this.companyForm.get('contactPersonName')?.invalid &&
      !this.companyForm.get('contactPersonRole')?.invalid &&
      !this.companyForm.get('contactPersonEmail')?.invalid &&
      !this.companyForm.get('contactPersonPhone')?.invalid
    )
  }

  onCancel(): void{
    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: "Unsaved changes will be lost if you navigate from this page. Do you want to continue?" }
    });

    dialog.afterClosed().subscribe((result: boolean) => {
      if(result){
        this.companyForm.reset();
        this.router.navigate(['/system-admin-dashboard/company']);
      }
    })
  }

  onSubmit() {
    
    if(this.companyForm.invalid) {
      return;
    }
    const { companyType, companyName, corporateMail, websiteURL, contactPersonName, contactPersonEmail, contactPersonRole,
      unitNum, streetNum, streetName, stateId, countyId, cityId, countryId, countryCode, zipCode, isActive, isDeleted } = this.companyForm.getRawValue();

      // ngx-intl-tel-input returns an object: { number: string, countryCode: CountryISO }
      const corporatePhoneValue = this.companyForm.get('corporatePhone')?.value ?? null;
      const contactPersonPhoneValue = this.companyForm.get('contactPersonPhone')?.value ?? null;

     


      // Extract phone number and country code from the object
      const corporatePhoneObj = corporatePhoneValue && typeof corporatePhoneValue === 'object' ? corporatePhoneValue : { number: corporatePhoneValue, countryCode: null };
      const contactPersonPhoneObj = contactPersonPhoneValue && typeof contactPersonPhoneValue === 'object' ? contactPersonPhoneValue : { number: contactPersonPhoneValue, countryCode: null };
      
      const corporatePhoneNumber = corporatePhoneObj.number || (corporatePhoneValue && typeof corporatePhoneValue === 'string' ? corporatePhoneValue : null);
      const contactPersonPhoneNumber = contactPersonPhoneObj.number || (contactPersonPhoneValue && typeof contactPersonPhoneValue === 'string' ? contactPersonPhoneValue : null);
      const corporatePhoneCountryCode = corporatePhoneObj.countryCode || this.companyForm.get('corporatePhoneCountryCode')?.value;
      const contactPersonPhoneCountryCode = contactPersonPhoneObj.countryCode || this.companyForm.get('contactPersonPhoneCountryCode')?.value;
      
   
      const corporatePhone = corporatePhoneNumber ? this.extractRawPhoneNumber(corporatePhoneNumber, corporatePhoneCountryCode || undefined) : null;
      const contactPersonPhone = contactPersonPhoneNumber ? this.extractRawPhoneNumber(contactPersonPhoneNumber, contactPersonPhoneCountryCode || undefined) : null;
      
      // Use the selected phone country code for the countryCode field in payload
      // Convert CountryISO to calling code (e.g., CountryISO.Afghanistan -> "+93")
      // Use corporate phone country code as primary, fallback to contact person phone country code
      const phoneCountryCode = corporatePhoneCountryCode || contactPersonPhoneCountryCode;
      const countryCodeForPayload = phoneCountryCode ? this.getCountryCallingCode(phoneCountryCode) : countryCode;
      
      // Get contactCountryCode from contactPersonPhoneCountryCode
      const contactCountryCodeForPayload = contactPersonPhoneCountryCode ? this.getCountryCallingCode(contactPersonPhoneCountryCode) : null;
      
      // Get ISO2 codes for CorporatePhoneCode and ContactPhoneCode
      const corporatePhoneCodeForPayload = corporatePhoneCountryCode ? String(corporatePhoneCountryCode) : null;
      const contactPhoneCodeForPayload = contactPersonPhoneCountryCode ? String(contactPersonPhoneCountryCode) : null;
      
    const payload: CompanyRequestPayload = {
      companyType, companyName, corporateMail, corporatePhone, websiteURL, contactPersonName, contactPersonEmail, contactPersonPhone, contactPersonRole,
      countryCode: countryCodeForPayload,
      contactCountryCode: contactCountryCodeForPayload,
      CorporatePhoneCode: corporatePhoneCodeForPayload,
      ContactPhoneCode: contactPhoneCodeForPayload,
      createdUserId: Number(this.authService.getUserDetailsFromStorage()?.createdUserId),
      isActive: isActive ? 1 : 0, isDeleted, createdDate: new Date().toISOString(),
      address: {
        unitNum, streetNum, streetName, stateId, countyId, cityId, countryId, zipCode, createdUserId: Number(this.authService.getUserDetailsFromStorage()?.createdUserId),
        createdDate: new Date().toISOString()
      }
    }; 
    this.isCreateComapny() ? this.createNewCompany(payload) : this.updateCompany(payload);
  }
  extractRawPhoneNumber(formattedPhoneNumber: string | null | undefined, countryCode?: CountryISO | null): string | null {
    if (typeof formattedPhoneNumber !== 'string') {
      return null;
    }
    
    // Extract all digits first
    let cleaned = formattedPhoneNumber.replace(/\D/g, '');
    
    if (!cleaned || cleaned.length === 0) {
      return null;
    }
    
    // Use provided countryCode or try to get from form fields
    const corporateCountryCode = this.companyForm.get('corporatePhoneCountryCode')?.value;
    const contactCountryCode = this.companyForm.get('contactPersonPhoneCountryCode')?.value;
    const selectedCountryCode = countryCode || corporateCountryCode || contactCountryCode || this.getCountryCodeFromCountryId(this.countryId);
    
    if (!selectedCountryCode) {
      return cleaned;
    }

    // Try to parse and extract national number
    try {
      const parsedNumber = this.phoneUtil.parse(cleaned, selectedCountryCode);
      
      // Get national number (without country code)
      const nationalNumber = parsedNumber.getNationalNumber();
      if (nationalNumber) {
        return String(nationalNumber);
      }
    } catch (error) {
      // Parsing failed - might be incomplete number, try to remove country code prefix manually
    }
    
    // Fallback: remove country code prefix if present
    const countryCodePrefix = this.getCountryCodePrefix(selectedCountryCode);
    if (countryCodePrefix) {
      const prefixDigits = countryCodePrefix.replace(/\D/g, '');
      // Check if the cleaned number starts with the country code prefix
      if (cleaned.startsWith(prefixDigits)) {
        // Remove the prefix to get national number
        cleaned = cleaned.substring(prefixDigits.length);
        // For UK (44), also remove leading 0 if present (UK national format starts with 0)
        if (selectedCountryCode === CountryISO.UnitedKingdom && cleaned.startsWith('0')) {
          cleaned = cleaned.substring(1);
        }
      }
    }
    
    return cleaned;
  }

  createNewCompany(payload: CompanyRequestPayload) {
    this.systemAdminService.addNewCompany(payload).subscribe({
      next: (response: any) => {
        if(response){
          const msg =  response?.message || "company added successfully";
          this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
          this.changesSaved = true;
          
          // Get companyId and configId from response
          const newCompanyId = response?.company?.companyId || response?.companyId;
          const configId = response?.company?.configId || response?.configId;
          
          // Update NJTN records if SLA numbers were added before company creation
          if (newCompanyId) {
            this.updateNJTNRecordsAfterCompanyCreation(newCompanyId);
          }
          
          // resposne success navigate to company list
          this.router.navigate(['/system-admin-dashboard/company']);
        }
      },
      error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }

  updateNJTNRecordsAfterCompanyCreation(companyId: number): void {
    const njtnList =
      (this.companyForm.get('producerSlaConfigList')?.value as unknown as any[]) ??
      (this.surplusLinesComponent ? this.surplusLinesComponent.getNjtnList() : []);
    if (!njtnList || njtnList.length === 0) {
      return;
    }

    // Filter only records that have configId but no companyId (added before company creation)
    const recordsToUpdate = njtnList.filter((item: any) => item?.configId && !item?.companyId);
    
    if (recordsToUpdate.length === 0) {
      return;
    }

    const updatedUserId = Number(this.authService.getUserDetailsFromStorage()?.createdUserId);
    const updatedDate = new Date().toISOString();

    // Prepare payload object for UpdateNJTN with companySLConfig array
    const updatePayload = {
      companySLConfig: recordsToUpdate.map((item: any) => ({
        configId: item.configId,
        companyId: companyId,
        slaNumber: item.slaNumber,
        isActive: true,
        isDeleted: false,
        updatedUserId: updatedUserId,
        updatedDate: updatedDate
      }))
    };

    // Call UpdateNJTN API with wrapped payload
    this.systemAdminService.UpdateNJTN(updatePayload).subscribe({
      next: (response: any) => {
        // Successfully updated
      },
      error: (error: any) => {
        console.error('Error updating NJTN records after company creation:', error);
        this.toastService.addNotification({
          message: 'Company created successfully, but failed to update SLA numbers. Please update them manually.',
          type: ToastType.Error
        });
      }
    });
  }

  updateCompany(payload: CompanyRequestPayload) {
    this.systemAdminService.updateCompany(payload, this.companyId()).subscribe({
      next: (response: any) => {
        if(response){
          const msg =  response?.message || "company added successfully";
          this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
          this.changesSaved = true;

          // Get companyId and configId from response
          const newCompanyId = response?.data?.companyId || response?.companyId;
          const configId = response?.data?.configId || response?.configId;
          
          // Update NJTN records if SLA numbers were added before company creation
          if (newCompanyId) {
            this.updateNJTNRecordsAfterCompanyCreation(newCompanyId);
          }

          // resposne success navigate to company list
           this.router.navigate(['/system-admin-dashboard/company']);
        }
      },
      error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    })
  }

  isFormDirty(): boolean {
    return JSON.stringify(this.initialFormValue) !== JSON.stringify(this.companyForm.getRawValue());
  }

  canDeactivate(): boolean {
    return this.changesSaved || !this.isFormDirty();
  }

  cancel(){
    this.router.navigate([''])
  }

  ngOnDestroy(): void {
    this.corporatePhoneSub.unsubscribe();
    this.contactPhoneSub.unsubscribe();
    this.countryIdSub.unsubscribe();
    this.stateIdSub.unsubscribe();
    this.companyTypeSub.unsubscribe();
    this.producerSlaNumbersSub.unsubscribe();
  }

  // Progressive live formatter for corporate/contact phones based on selected country code
  formatPhoneNumberLive(inputValue: any, isCorporatePhone: boolean = true): string {
    const raw = (inputValue ?? '').toString();
    if (!raw) return '';
    
    // Get the country code from the appropriate form field
    const countryCode = isCorporatePhone 
      ? this.companyForm.get('corporatePhoneCountryCode')?.value
      : this.companyForm.get('contactPersonPhoneCountryCode')?.value;
    
    if (!countryCode) {
      return raw;
    }
    
    // Extract raw digits
    const rawNumber = this.extractRawPhoneNumber(raw, countryCode);
    if (!rawNumber) return '';
    
    // Format using the selected country code
    const formatted = this.formatPhoneNumber(rawNumber, countryCode);
    return formatted || raw;
  }

  getMaxPhoneLength(countryCode: CountryISO | null | undefined): number {
    if (!countryCode) return 15;
    const regionCode = String(countryCode).toLowerCase();
    
    // Comprehensive country-to-max-length mapping
    const countryMaxLengths: { [key: string]: number } = {
      // Very short numbers (4-5 digits)
      'nu': 4,  // Niue
      'tk': 4,  // Tokelau
      'nf': 5,  // Norfolk Island
      
      // Short numbers (6-7 digits)
      'gi': 8,  // Gibraltar
      'va': 8,  // Vatican City
      'sm': 9,  // San Marino
      'mc': 9,  // Monaco
      'li': 9,  // Liechtenstein
      
      // Standard 10-digit numbers (US, Canada, etc.)
      'us': 10, // United States
      'ca': 10, // Canada
      'ru': 10, // Russia
      'kz': 10, // Kazakhstan
      'mx': 10, // Mexico
      'br': 11, // Brazil (can be 10 or 11)
      'in': 10, // India
      'au': 9,  // Australia
      'nz': 9,  // New Zealand
      'za': 9,  // South Africa
      'ar': 10, // Argentina
      'cl': 9,  // Chile
      'co': 10, // Colombia
      'pe': 9,  // Peru
      've': 10, // Venezuela
      'ec': 9,  // Ecuador
      'uy': 8,  // Uruguay
      'py': 9,  // Paraguay
      'bo': 8,  // Bolivia
      'cr': 8,  // Costa Rica
      'pa': 8,  // Panama
      'gt': 8,  // Guatemala
      'hn': 8,  // Honduras
      'ni': 8,  // Nicaragua
      'sv': 8,  // El Salvador
      'do': 10, // Dominican Republic
      'cu': 8,  // Cuba
      'jm': 10, // Jamaica
      'tt': 10, // Trinidad and Tobago
      'bb': 10, // Barbados
      'bs': 10, // Bahamas
      'bz': 7,  // Belize
      'gy': 7,  // Guyana
      'sr': 7,  // Suriname
      'gf': 9,  // French Guiana
      
      // 11-digit numbers (UK, etc.)
      'gb': 11, // United Kingdom
      'ie': 9,  // Ireland
      'nl': 9,  // Netherlands
      'be': 9,  // Belgium
      'lu': 9,  // Luxembourg
      'ch': 9,  // Switzerland
      'at': 10, // Austria
      'de': 11, // Germany
      'dk': 8,  // Denmark
      'se': 9,  // Sweden
      'no': 8,  // Norway
      'fi': 9,  // Finland
      'is': 7,  // Iceland
      'pl': 9,  // Poland
      'cz': 9,  // Czech Republic
      'sk': 9,  // Slovakia
      'hu': 9,  // Hungary
      'ro': 10, // Romania
      'bg': 9,  // Bulgaria
      'gr': 10, // Greece
      'pt': 9,  // Portugal
      'es': 9,  // Spain
      'it': 10, // Italy
      'mt': 8,  // Malta
      'cy': 8,  // Cyprus
      
      // Eastern Europe
      'ua': 9,  // Ukraine
      'by': 9,  // Belarus
      'lt': 8,  // Lithuania
      'lv': 8,  // Latvia
      'ee': 8,  // Estonia
      'md': 8,  // Moldova
      'rs': 9,  // Serbia
      'hr': 9,  // Croatia
      'si': 8,  // Slovenia
      'ba': 8,  // Bosnia and Herzegovina
      'mk': 9,  // North Macedonia
      'al': 9,  // Albania
      'me': 8,  // Montenegro
      'xk': 8,  // Kosovo
      
      // Middle East
      'sa': 9,  // Saudi Arabia
      'ae': 9,  // UAE
      'kw': 8,  // Kuwait
      'qa': 8,  // Qatar
      'bh': 8,  // Bahrain
      'om': 8,  // Oman
      'ye': 9,  // Yemen
      'iq': 10, // Iraq
      'ir': 10, // Iran
      'il': 9,  // Israel
      'jo': 9,  // Jordan
      'lb': 8,  // Lebanon
      'sy': 9,  // Syria
      'ps': 9,  // Palestine
      'tr': 10, // Turkey
      'eg': 10, // Egypt
      'ly': 9,  // Libya
      'tn': 8,  // Tunisia
      'dz': 9,  // Algeria
      'ma': 9,  // Morocco
      'sd': 9,  // Sudan
      'et': 9,  // Ethiopia
      
      // Asia
      'cn': 11, // China
      'jp': 10, // Japan
      'kr': 10, // South Korea
      'tw': 9,  // Taiwan
      'hk': 8,  // Hong Kong
      'mo': 8,  // Macau
      'sg': 8,  // Singapore
      'my': 9,  // Malaysia
      'th': 9,  // Thailand
      'vn': 10, // Vietnam
      'ph': 10, // Philippines
      'id': 9,  // Indonesia
      'mm': 9,  // Myanmar
      'kh': 9,  // Cambodia
      'la': 8,  // Laos
      'bn': 7,  // Brunei
      'bd': 10, // Bangladesh
      'pk': 10, // Pakistan
      'lk': 9,  // Sri Lanka
      'np': 10, // Nepal
      'af': 7,  // Afghanistan
      'uz': 9,  // Uzbekistan
      'tm': 8,  // Turkmenistan
      'tj': 9,  // Tajikistan
      'kg': 9,  // Kyrgyzstan
      'mn': 8,  // Mongolia
      
      // Oceania
      'pg': 8,  // Papua New Guinea
      'fj': 7,  // Fiji
      'nc': 6,  // New Caledonia
      'pf': 6,  // French Polynesia
      'ws': 7,  // Samoa
      'to': 7,  // Tonga
      'vu': 7,  // Vanuatu
      'sb': 7,  // Solomon Islands
      'ki': 5,  // Kiribati
      'tv': 6,  // Tuvalu
      'nr': 7,  // Nauru
      'pw': 7,  // Palau
      'fm': 7,  // Micronesia
      'mh': 7,  // Marshall Islands
      
      // Africa
      'ng': 10, // Nigeria
      'ke': 9,  // Kenya
      'tz': 9,  // Tanzania
      'ug': 9,  // Uganda
      'rw': 9,  // Rwanda
      'gh': 9,  // Ghana
      'ci': 8,  // Côte d'Ivoire
      'sn': 9,  // Senegal
      'ml': 8,  // Mali
      'bf': 8,  // Burkina Faso
      'ne': 8,  // Niger
      'td': 8,  // Chad
      'cm': 9,  // Cameroon
      'ga': 8,  // Gabon
      'cg': 9,  // Republic of the Congo
      'cd': 9,  // DR Congo
      'ao': 9,  // Angola
      'zm': 9,  // Zambia
      'zw': 9,  // Zimbabwe
      'bw': 8,  // Botswana
      'na': 9,  // Namibia
      'mw': 9,  // Malawi
      'mz': 9,  // Mozambique
      'mg': 9,  // Madagascar
      'mu': 8,  // Mauritius
      'sc': 7,  // Seychelles
      'km': 7,  // Comoros
      'dj': 8,  // Djibouti
      'er': 7,  // Eritrea
      'so': 8,  // Somalia
      'ss': 9,  // South Sudan
      
      // Caribbean
      'ht': 8,  // Haiti
      'pr': 10, // Puerto Rico
      'vi': 10, // US Virgin Islands
      'vg': 10, // British Virgin Islands
      'ky': 7,  // Cayman Islands
      'bm': 10, // Bermuda
      'tc': 7,  // Turks and Caicos
      'ai': 7,  // Anguilla
      'ms': 7,  // Montserrat
      'gd': 7,  // Grenada
      'lc': 7,  // Saint Lucia
      'vc': 7,  // Saint Vincent
      'ag': 10,  // Antigua and Barbuda
      'dm': 7,  // Dominica
      'kn': 7,  // Saint Kitts and Nevis
      
      // Other territories
      'fo': 6,  // Faroe Islands
      'gl': 6,  // Greenland
      'sj': 8,  // Svalbard
      'ax': 6,  // Åland Islands
      'gg': 10, // Guernsey
      'je': 10, // Jersey
      'im': 10, // Isle of Man
      'ad': 6,  // Andorra
    };
    
    return countryMaxLengths[regionCode] || 15; // Default to 15 for unknown countries
  }

  /**
   * Returns true when, for the current company, at least one SLA number
   * must be configured (US + New Jersey + broker house) but the grid is empty.
   * Used to disable Create/Save button when the SLA grid is required and empty.
   */
  isSlaGridRequiredAndEmpty(): boolean {
    const isBroker = this.hasSurplusTab();
    const requiresSla = isBroker && this.isUSAAndNewJerseySelected();
    if (!requiresSla) {
      return false;
    }

    const arr = this.companyForm.get('producerSlaNumbers') as FormArray | null;
    const length = arr ? arr.length : 0;
    return length === 0;
  }

  hasSurplusTab(): boolean {
    const companyTypeVal = this.companyForm.get('companyType')?.value;
    return String(companyTypeVal) === String(CompanyType.BROKER_COMPANY);
  }

  private loadUSStatesIfNeeded(): void {
    if (this.usStates.length > 0 && this.newJerseyStateId !== null) {
      return;
    }

    this.systemAdminService.getStateByCountryId(CountryType.US).subscribe({
      next: (states: any) => {
        if (Array.isArray(states)) {
          this.usStates = states as StateList[];
          const nj = this.usStates.find(
            (s) => s.stateName.toLowerCase() === 'new jersey'
          );
          this.newJerseyStateId = nj ? nj.stateId : null;
          this.updateSlaNumberValidator();
        }
      },
      error: () => {
        this.usStates = [];
        this.newJerseyStateId = null;
        this.updateSlaNumberValidator();
      }
    });
  }

  private isUSAAndNewJerseySelected(): boolean {
    const countryIdVal = this.companyForm.get('countryId')?.value;
    const stateIdVal = this.companyForm.get('stateId')?.value;

    if (Number(countryIdVal) !== CountryType.US) {
      return false;
    }

    const stateIdNum = stateIdVal !== null && stateIdVal !== undefined ? Number(stateIdVal) : NaN;
    if (!stateIdNum) {
      return false;
    }

    if (this.newJerseyStateId !== null) {
      return stateIdNum === this.newJerseyStateId;
    }

    if (this.usStates.length > 0) {
      const nj = this.usStates.find(
        (s) => s.stateName.toLowerCase() === 'new jersey'
      );
      if (nj) {
        this.newJerseyStateId = nj.stateId;
        return stateIdNum === nj.stateId;
      }
    }

    return false;
  }

  private updateSlaNumberValidator(): void {
    const slaCtrl = this.companyForm.get('slaNumber');
    if (!slaCtrl) {
      return;
    }

    const isBroker = this.hasSurplusTab();
    const requiresSla = isBroker && this.isUSAAndNewJerseySelected();
    const gridLength = (this.companyForm.get('producerSlaNumbers') as FormArray | null)?.length ?? 0;
    const hasAtLeastOneSlaInGrid = gridLength > 0;

    // SLA input should only be required when SLA grid is required AND currently empty.
    // Once at least one SLA is present in the grid, allow Create/Save even if the input is cleared.
    if (requiresSla && !hasAtLeastOneSlaInGrid) {
      slaCtrl.setValidators([
        Validators.required,
        noWhitespaceValidator,
        Validators.pattern(/^[a-zA-Z0-9]{5}$/)
      ]);
    } else {
      slaCtrl.setValidators([
        Validators.pattern(/^[a-zA-Z0-9]{5}$/)
      ]);
    }

    slaCtrl.updateValueAndValidity({ emitEvent: false });
  }
}
